import React, { useContext, useEffect, useReducer, useCallback } from "react";
import ReactFamilyTree from "react-family-tree";
import { IFamilyNode } from "relatives-tree/lib/types";
import { MainLayout } from "../../layouts";
import FamilyNode from "./FamilyNode";
import {
  ResponsiveContainer,
  PersonNode,
} from "../../components";
import styles from "./FamilyNode.module.css";
import { notification, Switch, Space, Typography } from "antd";
import { Backend, TreeHelper } from "../../helpers";
import { AxiosResponse, AxiosError } from "axios";
import { IFamilyTree, IFamilyTreeNode } from "../../interfaces";
import { authContext } from "../../providers";
import { IAuthContext } from "../../providers/AuthProvider";

const { MapInteractionCSS } = require("react-map-interaction");

const WIDTH = 200;
const HEIGHT = 200;

type FamilyTreeState = {
  rootId?: string;
  familyTree?: IFamilyTree;
  treeNodes: IFamilyTreeNode[];
  editMode: boolean;
  appContext: IAuthContext;
};

const FamilyTree: React.FC = (props) => {
  /**
   * States management
   */
  const reducer = (
    state: FamilyTreeState,
    action: {
      type: "updateState";
      state?: Partial<FamilyTreeState>;
    }
  ) => {
    switch (action.type) {
      case "updateState":
        return {
          editMode: action.state?.editMode ?? state.editMode,
          treeNodes: action.state?.treeNodes ?? state.treeNodes,
          familyTree: action.state?.familyTree ?? state.familyTree,
          rootId: action.state?.rootId ?? state.rootId,
          appContext: action.state?.appContext ?? state.appContext,
        };

      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, {
    editMode: true,
    treeNodes: [],
    appContext: useContext(authContext),
  });

  const setRootId = (id: string) => {
    dispatch({
      type: "updateState",
      state: {
        rootId: id,
      },
    });
  };

  const loadFamilyTree = useCallback(async () => {
    // update loading state
    state.appContext.dispatch({ type: "startLoading" });
    // fetch data
    await Backend.getInstance()
      .axios()
      .get("/search/tree/")
      .then((response: AxiosResponse<IFamilyTree>) => {
        const nodes = new TreeHelper(response.data, state.editMode).nodes;
        console.log({
          familyTree: response.data,
          treeNodes: nodes,
          rootId: nodes[0].id,
        });
        dispatch({
          type: "updateState",
          state: {
            familyTree: response.data,
            treeNodes: nodes,
            rootId: nodes[0].id,
          },
        });
        console.log("here we are");
      })
      .catch((reason: AxiosError<any>) => {
        notification.warn({
          message: `Failed to fetch your family tree. Try to reload this page please.`,
          placement: "topRight",
        });
      })
      .finally(() => {
        // update loading state
        state.appContext.dispatch({ type: "stopLoading" });
      });
  }, [state.editMode, state.appContext]);

  /**
   * Load tree when the component is mounted
   */
  useEffect(() => {
    loadFamilyTree();
  }, [loadFamilyTree]);

  return (
    <MainLayout fixed={true} className="bg-or_gray-100">
      <ResponsiveContainer>
        <div className="flex items-center flex-col my-4">
          <Typography.Title className="mb-4 mt-2" level={2}>
            {state.familyTree && state.familyTree.Person_set.length > 0
              ? "My Family Tree"
              : "Start a new tree"}
          </Typography.Title>
          <Space>
            <span>Edit mode: </span>
            <Switch
              defaultChecked={state.editMode}
              title="Family Tree Mode"
              disabled={
                !state.familyTree || state.familyTree.Person_set.length === 0
              }
              onChange={() => {
                dispatch({
                  type: "updateState",
                  state: {
                    editMode: !state.editMode,
                  },
                });
              }}
            />
          </Space>
        </div>

        <MapInteractionCSS showControls={true}>
          {state.treeNodes.length > 0 && state.rootId && (
            <ReactFamilyTree
              nodes={state.treeNodes as IFamilyNode[]}
              rootId={state.rootId}
              width={WIDTH}
              height={HEIGHT}
              className={`${styles.tree ?? ""}`}
              renderNode={(node) => {
                const customNode = node as IFamilyTreeNode;
                return (
                  <FamilyNode
                    key={node.id}
                    node={node}
                    isRoot={node.id === state.rootId}
                    onSubClick={setRootId}
                    style={{
                      width: WIDTH,
                      height: HEIGHT,
                      transform: `translate(${node.left * (WIDTH / 2)}px, ${
                        node.top * (HEIGHT / 2)
                      }px)`,
                    }}
                  >
                    <PersonNode
                      node={customNode}
                      onChange={() => {
                        loadFamilyTree();
                      }}
                    />
                  </FamilyNode>
                );
              }}
            />
          )}
        </MapInteractionCSS>
      </ResponsiveContainer>
    </MainLayout>
  );
};

export default FamilyTree;
